<template>
  <div class="runtime-shell" v-if="!tenantStore.isLoading">
    <!-- Header Removed: Injected via Dynamic Islands -->


    <main class="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[1400px] mx-auto px-4 py-6 w-full">
      <!-- Dynamic Region Rendering -->
      <div 
        v-for="(item, index) in tenantStore.regions.main" 
        :key="item.id + index" 
        class="island-wrapper w-full"
        :class="[
           (['HeaderIsland', 'FooterIsland', 'CustomizerIsland'].includes(item.id) || item.wrapper?.span === 2) ? 'md:col-span-2' : 'md:col-span-1',
           item.id !== 'HeaderIsland' ? 'bg-white rounded-xl shadow-sm overflow-hidden' : ''
        ]"
        :data-island-id="item.id"
      >
          <!-- Optional Section Header -->
          <div v-if="item.wrapper && item.wrapper.title" class="section-header">
             <h3 class="mb-5 border-b-2 border-lds-default pb-2">{{ item.wrapper.title }}</h3>
          </div>

          <!-- Component Injection -->
          <SurvivorBoundary :component-name="item.id" :is-admin="false">
            <component 
                :is="resolveComponent(item.id)" 
                v-bind="item.props"
                :variant="resolveVariant(item.id, item.props)"
                :currentUser="authStore.currentUser"
                :isGuestUser="authStore.isGuestUser"
                @login="authStore.login"
                @loginAsGuest="authStore.loginAsGuest"
                @save="handleSaveProject"
            />
          </SurvivorBoundary>
      </div>

      <!-- Debug Section -->
      <SurvivorBoundary component-name="RiskyIsland" :is-admin="true">
        <component :is="resolveComponent('DummyIsland')" title="Interactive Section (Click to Crash)" />
      </SurvivorBoundary>
    </main>

    <footer>
      <p>© 2026 AnimalMatch Platform</p>
    </footer>

    <!-- Global Modals -->
    <PaywallModal />
  </div>
  <div v-else>
    Loading Tenant...
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import SurvivorBoundary from './components/SurvivorBoundary.vue';
import PaywallModal from './components/PaywallModal.vue';

import { IslandRegistry } from './services/IslandRegistry';
import { useTenantStore } from './store/tenantStore';
import { useWizardStore } from '@emoji-photo/wizard-island'; 
import { auth } from './services/firebase';
import { UserService } from './services/UserService';
import { onAuthStateChanged } from 'firebase/auth';
import { initPreviewMode, previewState } from './services/previewMode';
import { useAuthStore } from './store/authStore';
import TL_HeaderIsland from './components/thelook/TL_HeaderIsland.vue';

export default {
  name: 'App',
  components: {
    SurvivorBoundary,
    TL_HeaderIsland,
    PaywallModal
  },
  setup(props) {
    initPreviewMode();
    const tenantStore = useTenantStore();
    const wizardStore = useWizardStore();
    const authStore = useAuthStore();
    const isSaving = ref(false);

    const resolveComponent = (id) => {
        const comp = IslandRegistry[id];
        if (!comp) return 'div'; // Fallback
        return comp;
    };

    const resolveVariant = (id, props) => {
        // 1. Check for live preview override
        if (previewState.islandVariants[id]) {
            return previewState.islandVariants[id];
        }
        // 2. Return from DB props or default
        return props?.variant || 'standard';
    };

    // Unified Funnel Persistence (Sprint A)
    wizardStore.$subscribe(() => {
        wizardStore.persistState();
    });

    // Auth & User Persistence (Sprint 4 - Phase 2)
    onMounted(() => {
        // Restore funnel state (unlocked steps, etc.)
        wizardStore.hydrate();

        // Initialize Auth (Centralized Logic)
        authStore.initializeAuth();

        // Inject Tenant Configuration into Island Store (Isolation Law)
        if (tenantStore.currentTenant) {
            wizardStore.setTenantConfig({
                animals: tenantStore.config.animals,
                heroIcon: tenantStore.config.heroIcon
            });
        }
    });

    const handleSaveProject = async () => {
        if (!authStore.currentUser) return;
        isSaving.value = true;
        try {
            // Prepare Project Data from Wizard Store
            const projectData = {
                name: `Collage ${new Date().toLocaleDateString()}`,
                tenantId: tenantStore.tenantName,
                assets: [],
                presets: {
                    theme: wizardStore.themePreset,
                    layout: wizardStore.layout,
                    spacing: wizardStore.spacing,
                    borderColor: wizardStore.borderColor
                }
            };
            
            await UserService.saveProject(authStore.currentUser.uid, projectData);
            alert('Project Saved to Cloud! ☁️');
        } catch (err) {
            console.error(err);
            alert('Failed to save project.');
        } finally {
            isSaving.value = false;
        }
    };

    return { 
        tenantStore, 
        resolveComponent, 
        resolveVariant, 
        authStore, 
        handleSaveProject, 
        isSaving 
    };
  }
}
</script>

<style>
/* Global resets that are not yet utility-first */
body {
  font-family: var(--font-family-lds-body);
  background-color: var(--color-lds-bg-main);
  color: var(--color-lds-text-main);
  max-width: none;
  margin: 0;
  padding: 0;
}
</style>
